import { useState } from 'react';
import dayjs from 'dayjs';

import PageContentPaper from 'components/layout/PageContentPaper';

import ReportDateRangeFilter from 'reports/shared/ReportDateRangeFilter';
import { useRetireeReportCounts } from './hooks/useRetireeReportCounts';
import PendingResponses from '../nonPlanYearServices/tiles/PendingResponses';
import ActiveParticipants from '../nonPlanYearServices/tiles/ActiveParticipants';
import PendingNotices from '../nonPlanYearServices/tiles/PendingNotices';
import CurrentActive from '../nonPlanYearServices/tiles/CurrentActive';
import NoticesMailed from '../nonPlanYearServices/tiles/NoticesMailed';
import AcceptanceReceived from '../nonPlanYearServices/tiles/AcceptanceReceived';
import PaymentsReceived from '../nonPlanYearServices/tiles/PaymentsReceived';
import MonthlyRecap from '../nonPlanYearServices/tiles/MonthlyRecap';
import InvoiceStatus from '../nonPlanYearServices/tiles/InvoiceStatus';
import ActiveWithAddress from '../nonPlanYearServices/tiles/ActiveWithAddress';
import TerminatedParticipants from '../nonPlanYearServices/tiles/TerminatedParticipants';
import Ten95CData from '../nonPlanYearServices/tiles/Ten95CData';
import TurningSixtyFive from '../nonPlanYearServices/tiles/TurningSixtyFive';
import NewEnrollments from '../nonPlanYearServices/tiles/NewEnrollments';
import OverDueInvoices from '../nonPlanYearServices/tiles/OverDueInvoices';

function RetireeBillingReports() {
  const [ startDate, setStartDate ] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [ endDate, setEndDate ] = useState<string>(dayjs().format('YYYY-MM-DD'));

  const counts = useRetireeReportCounts(startDate, endDate);

  function handleApply(start: string, end: string) {
    setStartDate(start);
    setEndDate(end);
  }

  return (
    <div className="w-full flex space-x-8">
      <PageContentPaper className="w-3/4">
        <div className="space-y-4">
          <div className="w-full">
            <ReportDateRangeFilter onFilterChange={handleApply} />
          </div>
          <div className="grid grid-flow-row grid-cols-3 grid-rows-4 gap-8">
            <NoticesMailed
              serviceType="retiree"
              count={counts?.noticesMailed ?? null}
              startDate={startDate}
              endDate={endDate}
            />
            <PaymentsReceived
              startDate={startDate}
              endDate={endDate}
              count={counts?.paymentsReceived ?? null}
              reportType="retiree"
            />
            <CurrentActive
              serviceType="retiree"
              description="This report looks for participants in the system during the date range entered and will
provide you with detail regarding those participants considered active with Event Dates within the date range."
              count={counts?.currentActive ?? null}
              startDate={startDate}
              endDate={endDate}
            />
            <MonthlyRecap
              count={counts?.monthlyRecap ?? null}
              serviceType="retiree"
              startDate={startDate}
              endDate={endDate}
            />
            <AcceptanceReceived
              serviceType="retiree"
              description="List of participants that have accepted coverage
and what plans they have elected. ** This report does not confirm payment received."
              count={counts?.acceptancesReceived ?? null}
              startDate={startDate}
              endDate={endDate}
            />
            <InvoiceStatus
              count={counts?.invoiceStatus ?? null}
              serviceType="retiree"
              startDate={startDate}
              endDate={endDate}
            />
            <ActiveWithAddress
              count={counts?.activeWithAddress ?? null}
              serviceType="retiree"
              description="List of active participants with their addresses."
              startDate={startDate}
              endDate={endDate}
            />
            <TerminatedParticipants
              count={counts?.termsFromRetiree ?? null}
              serviceType="retiree"
              description="List of participants that have terminated and their reason for termination
within the date range selected."
              startDate={startDate}
              endDate={endDate}
            />
            <Ten95CData
              count={counts?.ten95cData ?? null}
              startDate={startDate}
              endDate={endDate}
              reportType="retiree"
            />
            <TurningSixtyFive
              count={counts?.turningSixtyFive ?? null}
              serviceType="retiree"
              startDate={startDate}
              endDate={endDate}
            />
            <NewEnrollments
              count={counts?.newEnrollments ?? null}
              serviceType="retiree"
              startDate={startDate}
              endDate={endDate}
            />
            <OverDueInvoices
              count={counts?.overDueInvoices ?? null}
              serviceType="retiree"
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </div>
      </PageContentPaper>
      <div className="w-1/4 flex flex-col space-y-8">
        <PendingResponses serviceType="retiree" />
        <PendingNotices type="qe" serviceType="retiree" />
        <ActiveParticipants serviceType="retiree" />
      </div>
    </div>
  );
}

export default RetireeBillingReports;
